import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckSquare, Plus, Eye, EyeOff, Archive, ArchiveRestore, Trash2, Square, GripVertical } from "lucide-react";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import {
  listarNotasPessoais,
  criarNotaPessoal,
  atualizarNotaPessoal,
  excluirNotaPessoal,
  NotaPessoal,
} from "../services/notas";
import { renderTextoRico } from "../utils/textoComLinks";
import { MentionTextarea } from "./MentionTextarea";

/**
 * Lista de afazeres pessoal — só o próprio usuário enxerga (privacidade real
 * garantida no backend, filtra por req.user.id em todas as operações).
 *
 * Sem prazo, sem fluxo. Só checklist + arquivar. Útil pra "lembretes do dia"
 * que não merecem virar tarefa formal do sistema.
 */
export function NotasPessoaisCard() {
  const qc = useQueryClient();
  const [novoTexto, setNovoTexto] = useState("");
  const [verArquivadas, setVerArquivadas] = useState(false);

  const { data: notas = [], isLoading } = useQuery({
    queryKey: ["notas-pessoais", verArquivadas],
    queryFn: () => listarNotasPessoais({ arquivadas: verArquivadas }),
  });

  const criarM = useMutation({
    mutationFn: (texto: string) => criarNotaPessoal(texto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notas-pessoais"] });
      setNovoTexto("");
    },
  });

  const updateM = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: Parameters<typeof atualizarNotaPessoal>[1] }) =>
      atualizarNotaPessoal(id, dados),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notas-pessoais"] }),
  });

  const excluirM = useMutation({
    mutationFn: excluirNotaPessoal,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notas-pessoais"] }),
  });

  function adicionar() {
    const t = novoTexto.trim();
    if (!t) return;
    criarM.mutate(t);
  }

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-5 flex flex-col">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h2 className="font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
          <CheckSquare size={16} className="text-blue-600" />
          {verArquivadas ? "Arquivadas" : "Minhas anotações"}
        </h2>
        <button
          type="button"
          onClick={() => setVerArquivadas((v) => !v)}
          className="p-1.5 rounded text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
          title={verArquivadas ? "Voltar pras ativas" : "Ver arquivadas"}
          aria-label={verArquivadas ? "Voltar pras ativas" : "Ver arquivadas"}
        >
          {verArquivadas ? <Eye size={14} /> : <EyeOff size={14} />}
        </button>
      </div>

      {/* Add — mesmo editor rico dos comentários (jul/2026): a formatação
          aparece na hora (negrito/itálico/grifo/cor), sem os códigos na tela.
          usuarios=[] desliga o @menção (nota é pessoal). A nota é exibida com
          renderTextoRico, que lê o mesmo markup que este editor grava. */}
      {!verArquivadas && (
        <div className="mb-3">
          <MentionTextarea
            value={novoTexto}
            onChange={setNovoTexto}
            usuarios={[]}
            toolbar
            rows={1}
            onEnter={adicionar}
            placeholder="Anotar… (Enter adiciona · Shift+Enter nova linha)"
          />
          <div className="flex justify-end mt-1">
            <button
              type="button"
              onClick={adicionar}
              disabled={!novoTexto.trim() || criarM.isPending}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs disabled:opacity-40"
              title="Adicionar"
            >
              <Plus size={13} /> Adicionar
            </button>
          </div>
        </div>
      )}

      {/* Lista */}
      <div className="flex-1 min-h-0 overflow-y-auto -mx-1 px-1">
        {isLoading ? (
          <p className="text-xs text-slate-400 py-4 text-center">Carregando…</p>
        ) : notas.length === 0 ? (
          <p className="text-xs text-slate-400 py-6 text-center">
            {verArquivadas ? "Nada arquivado." : "Sem anotações. Use pra lembrar do que precisa fazer."}
          </p>
        ) : verArquivadas ? (
          // Arquivadas: lista simples, sem drag (não faz sentido reordenar)
          <div className="space-y-1">
            {notas.map((n) => (
              <ItemNota
                key={n.id}
                nota={n}
                onToggleConcluido={() => updateM.mutate({ id: n.id, dados: { concluido: !n.concluido } })}
                onArquivar={() => updateM.mutate({ id: n.id, dados: { arquivado: !n.arquivado } })}
                onEditar={(texto) => updateM.mutate({ id: n.id, dados: { texto } })}
                onExcluir={() => excluirM.mutate(n.id)}
              />
            ))}
          </div>
        ) : (
          <DragDropContext
            onDragEnd={(result: DropResult) => {
              if (!result.destination) return;
              if (result.destination.index === result.source.index) return;
              const arrastada = notas[result.source.index];
              const semArrastada = notas.filter((n) => n.id !== arrastada.id);
              const acima = semArrastada[result.destination.index - 1];
              const abaixo = semArrastada[result.destination.index];
              let novaOrdem: number;
              if (!acima && !abaixo) novaOrdem = 0;
              else if (!acima) novaOrdem = (abaixo!.ordem ?? 0) - 1000;
              else if (!abaixo) novaOrdem = (acima.ordem ?? 0) + 1000;
              else novaOrdem = ((acima.ordem ?? 0) + (abaixo.ordem ?? 0)) / 2;
              updateM.mutate({ id: arrastada.id, dados: { ordem: novaOrdem } });
            }}
          >
            <Droppable droppableId="notas">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-1">
                  {notas.map((n, index) => (
                    <Draggable key={n.id} draggableId={n.id} index={index}>
                      {(prov, snap) => (
                        <div
                          ref={prov.innerRef}
                          {...prov.draggableProps}
                          className={snap.isDragging ? "opacity-90" : ""}
                        >
                          <ItemNota
                            nota={n}
                            dragHandleProps={prov.dragHandleProps}
                            onToggleConcluido={() => updateM.mutate({ id: n.id, dados: { concluido: !n.concluido } })}
                            onArquivar={() => updateM.mutate({ id: n.id, dados: { arquivado: !n.arquivado } })}
                            onEditar={(texto) => updateM.mutate({ id: n.id, dados: { texto } })}
                            onExcluir={() => excluirM.mutate(n.id)}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>
    </div>
  );
}

function ItemNota({ nota, onToggleConcluido, onArquivar, onEditar, onExcluir, dragHandleProps }: {
  nota: NotaPessoal;
  onToggleConcluido: () => void;
  onArquivar: () => void;
  onEditar: (texto: string) => void;
  onExcluir: () => void;
  /** Quando passado, mostra a alça de arrastar à esquerda (só lista ativa). */
  dragHandleProps?: any;
}) {
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState(nota.texto);

  function commit() {
    const t = texto.trim();
    if (t && t !== nota.texto) onEditar(t);
    else setTexto(nota.texto);
    setEditando(false);
  }

  return (
    <div className="group flex items-center gap-2 px-1 py-1 rounded hover:bg-slate-50 dark:hover:bg-slate-800/50">
      {dragHandleProps && (
        <span
          {...dragHandleProps}
          className="shrink-0 text-slate-300 hover:text-slate-500 dark:text-slate-600 dark:hover:text-slate-400 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
          title="Arrastar para reordenar"
        >
          <GripVertical size={14} />
        </span>
      )}
      {!nota.arquivado && (
        <button
          type="button"
          onClick={onToggleConcluido}
          className="shrink-0 text-slate-400 hover:text-emerald-600"
          title={nota.concluido ? "Desmarcar" : "Marcar como feito"}
          aria-label={nota.concluido ? "Desmarcar" : "Marcar como feito"}
        >
          {nota.concluido ? <CheckSquare size={16} className="text-emerald-600" /> : <Square size={16} />}
        </button>
      )}

      {editando ? (
        // Edição com o mesmo editor rico (antes era um input cru sem formatação
        // nenhuma). Botões explícitos porque, com a barra de formatação, commit
        // no blur fecharia a edição ao clicar num botão de formato.
        <div className="flex-1 min-w-0">
          <MentionTextarea
            value={texto}
            onChange={setTexto}
            usuarios={[]}
            toolbar
            rows={1}
            onEnter={commit}
            placeholder="Editar anotação…"
          />
          <div className="flex items-center gap-2 mt-1">
            <button type="button" onClick={commit} className="text-xs px-2.5 py-1 rounded bg-blue-600 text-white hover:bg-blue-700">Salvar</button>
            <button type="button" onClick={() => { setTexto(nota.texto); setEditando(false); }} className="text-xs px-2.5 py-1 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200">Cancelar</button>
          </div>
        </div>
      ) : (
        // Texto da nota: clique no espaço vazio entra em edição, mas cliques em
        // links seguem ao destino (handler deles tem stopPropagation no util).
        <span
          onClick={(e) => {
            if ((e.target as HTMLElement).tagName === "A") return;
            if (!nota.arquivado) setEditando(true);
          }}
          className={`flex-1 text-sm truncate cursor-text [overflow-wrap:anywhere] ${
            nota.concluido ? "line-through text-slate-400 dark:text-slate-500" : "text-slate-700 dark:text-slate-200"
          } ${nota.arquivado ? "italic text-slate-400 dark:text-slate-500" : ""}`}
          title={nota.texto}
        >
          {renderTextoRico(
            nota.texto,
            "underline decoration-blue-400 underline-offset-2 text-blue-600 hover:text-blue-700",
          )}
        </span>
      )}

      <div className="shrink-0 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          type="button"
          onClick={onArquivar}
          className="p-1 rounded text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
          title={nota.arquivado ? "Desarquivar" : "Arquivar"}
          aria-label={nota.arquivado ? "Desarquivar" : "Arquivar"}
        >
          {nota.arquivado ? <ArchiveRestore size={13} /> : <Archive size={13} />}
        </button>
        <button
          type="button"
          onClick={onExcluir}
          className="p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
          title="Excluir definitivamente"
          aria-label="Excluir"
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}
